import { Button, Dropdown, Space, Pagination, Divider, Modal } from "antd";
import SearchInput from "../../components/Forms/SearchInput";
import { useEffect, useState } from "react";
import { useApiCall } from "../../services/api";
import {
  useNavigate,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import { formatDate, handleRouteNav } from "../../utils";
import Loader from "../../common/Loader";
import PrimaryButton from "../../components/Forms/PrimaryButton";
import Filters from "../../components/Filters";
import CustomBadge from "../../components/Charts/CustomBadge";
import {
  modificationTitlesTags,
  perPageOptions,
  roleNames,
  showDevLogs,
} from "../../constant";
import { useSelector } from "react-redux";
import { PiPencilSimple, PiTrash } from "react-icons/pi";
import NewAnnouncement from "../../components/models/NewAnnouncement";
import ConfirmModal from "../../components/models/ConfirmModal";
import usePagination from "../../hooks/usePagination";

const Announcements = () => {
  const { appliedFilters, DrawerUI, FilterButton } = Filters({
    filters: [
      {
        type: "select",
        key: "category",
        label: "Category",
        options: [
          {
            label: modificationTitlesTags.pendingTitle,
            value: modificationTitlesTags.pendingTitle,
          },
          {
            label: modificationTitlesTags.updateTitle,
            value: modificationTitlesTags.updateTitle,
          },
        ],
      },
      {
        type: "select",
        key: "status",
        label: "Status",
        options: [
          { label: "Approved", value: "approved" },
          { label: "Rejected", value: "rejected" },
          { label: "Pending", value: "pending" },
        ],
      },
      {
        type: "daterange",
        key: "daterange",
        label: "Date",
      },
    ],
    onChange: () => resetPage(),
  });
  const { loading, postApi } = useApiCall();
  const [data, setData] = useState([]);
  const { pagination, setPagination, resetPage } = usePagination();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isNewAnnouncementModalVisible, setIsNewAnnouncementModalVisible] =
    useState(false);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState({});
  const { listRefreshKey } = useSelector((state) => state.cartRefresh);

  const navigate = useNavigate();
  const query = searchParams.get("q") || "";
  const roleOrder = Object.keys(roleNames);

  const onSearch = (value, _e, info) => console.log(info?.source, value);

  const handleOnSearchChange = (e) => {
    const value = e.target.value;
    setSearchParams(
      (prev) => {
        if (value) prev.set("q", value);
        else prev.delete("q");
        return prev;
      },
      { replace: true },
    );
    resetPage();
  };

  const fetchModificationLists = async () => {
    const { status, category, daterange } = appliedFilters;
    try {
      const filters = {
        status: status,
        category: category,
        date_from: daterange?.[0]
          ? formatDate(new Date(daterange[0]), { format: "YYYY-MM-DD" })
          : undefined,
        date_to: daterange?.[1]
          ? formatDate(new Date(daterange[1]), { format: "YYYY-MM-DD" })
          : undefined,
      };

      const res = await postApi("announcements/get", {
        search: query,
        page: pagination.current,
        per_page: pagination.pageSize,
        ...(Object.keys(appliedFilters).length ? filters : {}),
      });

      setData(res.data || []);
      setPagination({ total: res.total || 0 });
    } catch (error) {
      console.error("Error fetching modification lists:", error);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchModificationLists();
    }, 300);

    return () => clearTimeout(handler);
  }, [
    query,
    pagination.current,
    pagination.pageSize,
    appliedFilters,
    listRefreshKey,
  ]);

  const handleNewAnnouncementOk = async (values) => {
    try {
      const response = await postApi(
        "announcements/update",
        {
          ...values,
          announcement_roles: Array.isArray(values.announcement_roles)
            ? values.announcement_roles.join(",")
            : values.announcement_roles,
          announcement_id: selectedRecord?.announcement_id,
        },
        true,
      );
      if (response?.status) {
        setIsNewAnnouncementModalVisible(false);
        fetchModificationLists();
      }

      return response?.status || false;
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const handleConfirmAction = async (action) => {
    try {
      const response = await postApi(
        "announcements/delete",
        { announcement_id: selectedRecord?.announcement_id },
        true,
      );
      if (response?.status) {
        fetchModificationLists();
        setIsConfirmModalVisible(false);
        setSelectedRecord({});
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const { setTitle, setDescription, setBackButton } = useOutletContext();

  useEffect(() => {
    setTitle("Announcements");
    setDescription(`${pagination.total} total announcements`);
    setBackButton(false);
  }, [setTitle, setDescription, setBackButton, pagination.total]);

  return (
    <>
      <DrawerUI />
      <>
        <div className="flex flex-col pb-10">
          {/* <div className="bg-[#FFFFFF] mb-6" style={{ borderRadius: "32px", overflow: "hidden" }}>
            <div className="flex justify-between gap-7 items-center px-8 py-6">
              <SearchInput
                onSearch={onSearch}
                value={query}
                onChange={handleOnSearchChange}
                placeholder="Search for modifications"
              />
              <div className="flex gap-3 items-center">
                <FilterButton />
              </div>
            </div>
          </div> */}

          {/* Loader Wrapper */}
          {loading ? (
            <Loader fullScreen={false} /> // ✅ Use custom Loader
          ) : (
            <>
              {data && data.length > 0 ? (
                data.map((record, i) => {
                  return (
                    <div
                      key={i}
                      className="bg-[#ffffff] p-6 rounded-3xl border border-[#D9D9D9] mb-6 hover:cursor-pointer hover:shadow-md transition-all transform"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedRecord(record);
                        setIsDetailsModalVisible(true);
                      }}
                    >
                      {/* Header */}
                      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-5 pr-[35px]">
                        <div className="flex items-center gap-4">
                          <div className="flex flex-col gap-2 text-black">
                            <span className="font-semibold">
                              {formatDate(record?.updated_at, {
                                format: "dddd",
                              })}
                            </span>
                            <span>
                              {formatDate(record?.updated_at, {
                                format: "DD/MM/YYYY",
                              })}
                            </span>
                            {record?.updated_at !== record?.created_at && (
                              <span className="text-[#A33434]">Edited</span>
                            )}
                          </div>
                          <Divider type="vertical" />
                          <div className="flex flex-col text-start justify-start">
                            <h3 className="text-xl font-semibold text-[#A33434]">
                              {record.announcement_title}
                            </h3>
                            <div className="w-full flex flex-col items-start justify-start gap-4">
                              <p className="text-sm text-gray-600 whitespace-pre-line">
                                {record.announcement_desc?.replace(
                                  /\\n/g,
                                  "\n",
                                )}
                              </p>

                              <p className="text-md text-black ">
                                <span className="font-semibold">Users:</span>{" "}
                                <span className="italic">
                                  {record?.announcement_roles
                                    ?.split(",")
                                    .map((role) => role.trim())
                                    .filter(Boolean)
                                    .sort((a, b) => {
                                      const aIndex = roleOrder.indexOf(a);
                                      const bIndex = roleOrder.indexOf(b);

                                      if (aIndex === -1 && bIndex === -1) {
                                        return a.localeCompare(b);
                                      }
                                      if (aIndex === -1) return 1;
                                      if (bIndex === -1) return -1;

                                      return aIndex - bIndex;
                                    })
                                    .map((role) => roleNames[role] || role)
                                    .join(", ")}
                                </span>
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Status + Button */}
                        <div className="flex items-center gap-x-4">
                          <Button
                            type="primary"
                            className="rounded-full w-8 h-8 flex items-center justify-center p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedRecord(record);
                              setIsConfirmModalVisible(true);
                            }}
                          >
                            <PiTrash size={20} />
                          </Button>
                          <Button
                            type="primary"
                            className="rounded-full w-8 h-8 flex items-center justify-center p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedRecord(record);
                              setIsNewAnnouncementModalVisible(true);
                            }}
                          >
                            <PiPencilSimple size={20} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-center text-gray-500">No records found.</p>
              )}

              {/* Pagination */}
              <div className="flex justify-end mt-6 pr-6">
                <Pagination
                  current={pagination.current}
                  showSizeChanger={true}
                  pageSizeOptions={perPageOptions}
                  pageSize={pagination.pageSize}
                  showTotal={(total, range) =>
                    `Showing ${range[0]}-${range[1]} of ${total} items`
                  }
                  onChange={(newPage, newPageSize) => {
                    setPagination((prev) => ({
                      current: newPageSize !== prev.pageSize ? 1 : newPage,
                      pageSize: newPageSize,
                    }));
                  }}
                  total={pagination.total}
                />
              </div>
            </>
          )}
        </div>
      </>
      <NewAnnouncement
        isOpen={isNewAnnouncementModalVisible}
        closeModal={() => setIsNewAnnouncementModalVisible(false)}
        onConfirm={handleNewAnnouncementOk}
        data={selectedRecord}
        title={
          selectedRecord?.announcement_id
            ? "Edit Announcement"
            : "Add Announcement"
        }
        loading={loading}
      />

      <ConfirmModal
        actionKey={"GENERIC_CONFIRM"}
        record={selectedRecord}
        open={isConfirmModalVisible}
        onCancel={() => {
          setIsConfirmModalVisible(false);
          setSelectedRecord({});
        }}
        onConfirm={handleConfirmAction}
        loading={loading}
        description="Do you want to delete this announcement?"
        title="Delete Announcement"
        confirmText="Delete Announcement"
      />

      <Modal
        open={isDetailsModalVisible}
        footer={null}
        onCancel={() => {
          setIsDetailsModalVisible(false);
          setSelectedRecord({});
        }}
        className="form-css"
        width={600}
      >
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-5 p-3">
          <div className="flex items-center gap-4">
            <div className="flex flex-col gap-2 text-black">
              <span className="font-semibold">
                {formatDate(selectedRecord.updated_at, { format: "dddd" })}
              </span>
              <span>
                {formatDate(selectedRecord.updated_at, {
                  format: "DD/MM/YYYY",
                })}
              </span>
            </div>
            <Divider type="vertical" />
            <div className="flex flex-col justify-start">
              <h3 className="text-start text-xl font-semibold text-[#A33434]">
                {selectedRecord.announcement_title}
              </h3>
              <div className="w-full flex flex-col justify-start items-start gap-4">
                <p className="text-sm text-gray-600 whitespace-pre-line">
                  {selectedRecord.announcement_desc?.replace(/\\n/g, "\n")}
                </p>
                <p className="text-md text-black">
                  <span className="font-semibold normal-case">Users:</span>{" "}
                  <span className="italic">
                    {selectedRecord?.announcement_roles
                      ?.split(",")
                      .map((role) => role.trim())
                      .filter(Boolean)
                      .sort((a, b) => {
                        const aIndex = roleOrder.indexOf(a);
                        const bIndex = roleOrder.indexOf(b);

                        if (aIndex === -1 && bIndex === -1) {
                          return a.localeCompare(b);
                        }
                        if (aIndex === -1) return 1;
                        if (bIndex === -1) return -1;

                        return aIndex - bIndex;
                      })
                      .map((role) => roleNames[role] || role)
                      .join(", ")}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Announcements;
